import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  VStack,
  HStack,
  Text,
  Input,
  Flex,
  Avatar,
  Spinner,
  Button,
  Heading,
  Tag,
  TagLabel,
  Image,
  Link
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FaPrayingHands, FaPaperPlane, FaTimes, FaGlobe } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../config/axios';

const MotionBox = motion(Box);

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(212, 175, 55, 0); }
  100% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0); }
`;

const AITempleAssistant = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [language, setLanguage] = useState('en'); // 'en' or 'te'
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);

  // Localization resources
  const t = {
    en: {
      title: 'AI Temple Assistant',
      subtitle: 'Sri Renuka Ankamma Talli Devasthanam',
      placeholder: 'Ask about timings, poojas, festivals...',
      welcome: 'Namaste! I am your Sri Renuka Temple Assistant. How can I help you today?',
      timingsChip: '🕒 Timings',
      poojasChip: '🙏 Poojas',
      festivalsChip: '📅 Festivals',
      donationsChip: '💳 Donate',
      addressChip: '📍 Location',
      activeLang: 'English',
      errorMsg: 'Could not connect. Please try again.',
      quickRepliesLabel: 'Quick Options:'
    },
    te: {
      title: 'ఆలయ ఏఐ సహాయకుడు',
      subtitle: 'శ్రీ రేణుక దేవస్థానము',
      placeholder: 'సమయాలు, పూజలు, పండుగల గురించి అడగండి...',
      welcome: 'నమస్తే! నేను మీ శ్రీ రేణుక ఆలయ సహాయకుడిని. ఈరోజు నేను మీకు ఎలా సహాయపడగలను?',
      timingsChip: '🕒 సమయాలు',
      poojasChip: '🙏 పూజలు',
      festivalsChip: '📅 పండుగలు',
      donationsChip: '💳 విరాళాలు',
      addressChip: '📍 చిరునామా',
      activeLang: 'తెలుగు',
      errorMsg: 'కనెక్ట్ చేయడం కుదరలేదు. దయచేసి మళ్ళీ ప్రయత్నించండి.',
      quickRepliesLabel: 'త్వరిత ఎంపికలు:'
    }
  };

  const currentT = t[language];

  // Helper to parse markdown links: [text](url)
  const renderMessageText = (text) => {
    if (!text) return null;
    
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    markdownLinkRegex.lastIndex = 0;
    
    while ((match = markdownLinkRegex.exec(text)) !== null) {
      const matchIndex = match.index;
      
      if (matchIndex > lastIndex) {
        parts.push(text.substring(lastIndex, matchIndex));
      }
      
      const linkText = match[1];
      const linkUrl = match[2];
      
      parts.push(
        <Link
          key={matchIndex}
          href={linkUrl}
          isExternal
          color="#FF9933"
          textDecoration="underline"
          fontWeight="bold"
          _hover={{ color: '#e08020' }}
        >
          {linkText}
        </Link>
      );
      
      lastIndex = markdownLinkRegex.lastIndex;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? (
      <Text fontSize="13.5px" whiteSpace="pre-line" lineHeight="1.5">
        {parts}
      </Text>
    ) : (
      <Text fontSize="13.5px" whiteSpace="pre-line" lineHeight="1.5">
        {text}
      </Text>
    );
  };

  // Initialize with welcome message on mount or language change
  useEffect(() => {
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        text: currentT.welcome,
        timestamp: new Date()
      }
    ]);
  }, [language, currentT.welcome]);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener('open-ai-assistant', handleOpen);
    return () => window.removeEventListener('open-ai-assistant', handleOpen);
  }, []);

  const getTempleStatus = () => {
    const now = new Date();
    // Convert to IST (UTC + 5:30)
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const istTime = new Date(utc + (3600000 * 5.5));
    const hours = istTime.getHours();
    const minutes = istTime.getMinutes();
    const decimalTime = hours + minutes / 60;

    // Morning hours: 6:00 AM - 12:30 PM (6.0 to 12.5)
    // Evening hours: 4:00 PM - 8:30 PM (16.0 to 20.5)
    const isMorningOpen = decimalTime >= 6.0 && decimalTime <= 12.5;
    const isEveningOpen = decimalTime >= 16.0 && decimalTime <= 20.5;

    if (isMorningOpen) {
      return {
        isOpen: true,
        text: {
          en: 'OPEN NOW (Closes at 12:30 PM)',
          te: 'ప్రస్తుతం తెరిచి ఉంది (12:30 PM కి మూసివేయబడును)'
        },
        color: '#48BB78' // green.400
      };
    } else if (isEveningOpen) {
      return {
        isOpen: true,
        text: {
          en: 'OPEN NOW (Closes at 8:30 PM)',
          te: 'ప్రస్తుతం తెరిచి ఉంది (8:30 PM కి మూసివేయబడును)'
        },
        color: '#48BB78' // green.400
      };
    } else {
      let nextOpening = '';
      if (decimalTime < 6.0) {
        nextOpening = { en: 'Opens at 6:00 AM today', te: 'ఈరోజు ఉదయం 6:00 AM కి తెరువబడును' };
      } else if (decimalTime > 12.5 && decimalTime < 16.0) {
        nextOpening = { en: 'Opens at 4:00 PM today', te: 'ఈరోజు సాయంత్రం 4:00 PM కి తెరువబడును' };
      } else {
        nextOpening = { en: 'Opens at 6:00 AM tomorrow', te: 'రేపు ఉదయం 6:00 AM కి తెరువబడును' };
      }
      return {
        isOpen: false,
        text: {
          en: `CLOSED NOW (${nextOpening.en})`,
          te: `ప్రస్తుతం మూసివేసి ఉంది (${nextOpening.te})`
        },
        color: '#E53E3E' // red.500
      };
    }
  };

  const handleSendMessage = async (textToSend) => {
    const queryText = textToSend || inputValue;
    if (!queryText.trim()) return;

    if (!textToSend) {
      setInputValue('');
    }

    // Append user message
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: queryText,
      timestamp: new Date()
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Map message history to send to backend
      const history = messages
        .filter(m => m.id !== 'welcome')
        .map(m => ({
          role: m.role,
          text: m.text
        }));

      const response = await api.post('/api/ai/chat', {
        message: queryText,
        language: language,
        history: history,
        templeStatus: getTempleStatus().text[language]
      });

      const aiResponse = response.data?.response;

      const userQuery = queryText.toLowerCase();
      const showQR = userQuery.includes('donate') || 
                     userQuery.includes('donation') || 
                     userQuery.includes('hundi') || 
                     userQuery.includes('annadanam') || 
                     userQuery.includes('విరాళం') || 
                     userQuery.includes('డబ్బు') || 
                     userQuery.includes('qrcode');

      setMessages((prev) => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          text: aiResponse || currentT.errorMsg,
          timestamp: new Date(),
          image: showQR ? '/qrcode.png.jpeg' : undefined
        }
      ]);
    } catch (error) {
      console.error('AI Chat Error:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          text: currentT.errorMsg,
          timestamp: new Date(),
          isError: true
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'te' : 'en'));
  };

  const quickReplies = [
    { label: currentT.timingsChip, query: language === 'en' ? 'temple timings' : 'ఆలయ సమయాలు' },
    { label: currentT.poojasChip, query: language === 'en' ? 'pooja details' : 'పూజలు వివరాలు' },
    { label: currentT.festivalsChip, query: language === 'en' ? 'festival schedule' : 'పండుగలు షెడ్యూల్' },
    { label: currentT.donationsChip, query: language === 'en' ? 'how to donate' : 'విరాళాలు ఎలా ఇవ్వాలి' },
    { label: currentT.addressChip, query: language === 'en' ? 'temple location address' : 'ఆలయ చిరునామా' }
  ];

  return (
    <AnimatePresence mode="wait">
      {!isOpen ? (
        <Box position="fixed" bottom="145px" right="25px" zIndex={1000}>
          <MotionBox
            key="trigger"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <IconButton
              aria-label="Open AI Assistant"
              icon={<FaPrayingHands />}
              onClick={() => setIsOpen(true)}
              colorScheme="yellow"
              bg="#FF9933"
              color="white"
              borderRadius="full"
              boxSize="50px"
              fontSize="20px"
              boxShadow="0 4px 20px rgba(255, 153, 51, 0.4)"
              animation={`${pulseGlow} 2s infinite`}
              _hover={{ bg: '#e08020' }}
            />
          </MotionBox>
        </Box>
      ) : (
        <Box position="fixed" bottom="30px" right="25px" zIndex={1000}>
          <MotionBox
            key="panel"
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            w={{ base: '320px', md: '380px' }}
            h={{ base: '450px', md: '500px' }}
            bg="rgba(255, 255, 255, 0.95)"
            backdropFilter="blur(10px)"
            borderRadius="2xl"
            boxShadow="0 10px 30px rgba(128, 0, 32, 0.15), 0 1px 1px rgba(0,0,0,0.05)"
            border="1px solid"
            borderColor="#EAD8C0"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            {/* Header */}
            <Flex
              bg="#800020"
              p={4}
              align="center"
              justify="space-between"
              borderBottom="3px solid"
              borderColor="#D4AF37"
            >
              <HStack spacing={3}>
                <Avatar size="sm" bg="#FF9933" icon={<FaPrayingHands />} />
                <VStack align="start" spacing={0.5}>
                  <Heading size="xs" color="#FFD700" fontFamily="'Georgia', serif">
                    {currentT.title}
                  </Heading>
                  <Text fontSize="9px" color="whiteAlpha.800" fontWeight="600" letterSpacing="wide">
                    {currentT.subtitle}
                  </Text>
                  <HStack spacing={1} pt={0.5}>
                    <Box w="6px" h="6px" bg={getTempleStatus().color} borderRadius="full" />
                    <Text fontSize="8px" fontWeight="bold" color="whiteAlpha.900">
                      {getTempleStatus().text[language]}
                    </Text>
                  </HStack>
                </VStack>
              </HStack>

              <HStack spacing={1}>
                {/* Language Switch Button */}
                <Button
                  size="xs"
                  variant="outline"
                  borderColor="whiteAlpha.600"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  leftIcon={<FaGlobe />}
                  onClick={toggleLanguage}
                  fontSize="10px"
                  h="24px"
                  px={2}
                >
                  {language === 'en' ? 'తెలుగు' : 'English'}
                </Button>

                {/* Close Button */}
                <IconButton
                  size="sm"
                  variant="ghost"
                  color="white"
                  icon={<FaTimes />}
                  _hover={{ bg: 'whiteAlpha.200' }}
                  onClick={() => setIsOpen(false)}
                  aria-label="Close Chat"
                />
              </HStack>
            </Flex>

            {/* Message Thread */}
            <Box flex={1} overflowY="auto" p={4} css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: '#FAF6F0' },
              '&::-webkit-scrollbar-thumb': { background: '#EAD8C0', borderRadius: '4px' },
            }}>
              <VStack spacing={4} align="stretch">
                {messages.map((message) => {
                  const isUser = message.role === 'user';
                  return (
                    <Flex
                      key={message.id}
                      justify={isUser ? 'flex-end' : 'flex-start'}
                    >
                      <HStack
                        spacing={2}
                        maxW="80%"
                        align="start"
                        flexDirection={isUser ? 'row-reverse' : 'row'}
                      >
                        {!isUser && (
                          <Avatar size="xs" bg="#FF9933" icon={<FaPrayingHands />} />
                        )}
                        <Box
                          bg={isUser ? '#FF9933' : message.isError ? 'red.500' : '#FAF6F0'}
                          color={isUser ? 'white' : message.isError ? 'white' : 'gray.800'}
                          px={3.5}
                          py={2}
                          borderRadius="xl"
                          borderTopRightRadius={isUser ? 'none' : 'xl'}
                          borderTopLeftRadius={isUser ? 'xl' : 'none'}
                          boxShadow="sm"
                          border={!isUser ? '1px solid' : 'none'}
                          borderColor="#EAD8C0"
                        >
                          {renderMessageText(message.text)}
                          {message.image && (
                            <Image
                              src={message.image}
                              alt="Donation QR Code"
                              mt={3}
                              borderRadius="md"
                              maxW="200px"
                              border="1px solid"
                              borderColor="gray.200"
                              mx="auto"
                            />
                          )}
                          <Text
                            fontSize="9px"
                            color={isUser ? 'whiteAlpha.800' : 'gray.500'}
                            textAlign={isUser ? 'right' : 'left'}
                            mt={1}
                          >
                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Text>
                        </Box>
                      </HStack>
                    </Flex>
                  );
                })}

                {/* Loading Indicator */}
                {isLoading && (
                  <Flex justify="flex-start">
                    <HStack spacing={2} align="center">
                      <Avatar size="xs" bg="#FF9933" icon={<FaPrayingHands />} />
                      <Box
                        bg="#FAF6F0"
                        px={4}
                        py={2}
                        borderRadius="xl"
                        borderTopLeftRadius="none"
                        border="1px solid"
                        borderColor="#EAD8C0"
                      >
                        <HStack spacing={1}>
                          <Spinner size="xs" color="#800020" />
                          <Text fontSize="11px" color="gray.500">Thinking...</Text>
                        </HStack>
                      </Box>
                    </HStack>
                  </Flex>
                )}

                <div ref={messagesEndRef} />
              </VStack>
            </Box>

            {/* Quick Replies / Suggestions */}
            <Box px={4} py={2} bg="#FAF6F0" borderTop="1px solid" borderColor="#EAD8C0">
              <Text fontSize="10px" color="gray.500" mb={1.5} fontWeight="bold">
                {currentT.quickRepliesLabel}
              </Text>
              <Flex gap={2} wrap="wrap">
                {quickReplies.map((reply, index) => (
                  <Tag
                    key={index}
                    size="sm"
                    variant="solid"
                    bg="white"
                    color="#800020"
                    border="1px solid"
                    borderColor="#EAD8C0"
                    borderRadius="full"
                    cursor="pointer"
                    _hover={{ bg: '#FF9933', color: 'white', borderColor: '#FF9933' }}
                    onClick={() => !isLoading && handleSendMessage(reply.query)}
                    px={3}
                    py={1}
                  >
                    <TagLabel fontSize="11px" fontWeight="600">{reply.label}</TagLabel>
                  </Tag>
                ))}
              </Flex>
            </Box>

            {/* Input Footer */}
            <Box p={3} borderTop="1px solid" borderColor="#EAD8C0" bg="white">
              <HStack spacing={2}>
                <Input
                  placeholder={currentT.placeholder}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  size="sm"
                  h="38px"
                  borderRadius="full"
                  bg="#FAF6F0"
                  border="1px solid"
                  borderColor="#EAD8C0"
                  _focus={{ borderColor: '#FF9933', bg: 'white', boxShadow: '0 0 0 1px #FF9933' }}
                  fontSize="13px"
                  isDisabled={isLoading}
                />
                <IconButton
                  colorScheme="yellow"
                  bg="#800020"
                  color="white"
                  icon={<FaPaperPlane />}
                  borderRadius="full"
                  size="sm"
                  boxSize="38px"
                  _hover={{ bg: '#600018' }}
                  onClick={() => handleSendMessage()}
                  isLoading={isLoading}
                  aria-label="Send Message"
                />
              </HStack>
            </Box>
          </MotionBox>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default AITempleAssistant;
